import { collection, addDoc, setDoc, doc } from "firebase/firestore";
import { db } from "../firebaseInit";
import { v4 as uuidv4 } from 'uuid';

/**
 * Migrates demo data from localStorage to Firebase for a newly created user account
 * @param {string} userId - The user's Firebase UID
 * @returns {Promise<Object>} - Migration results with success status and counts
 */
export const migrateDemoDataToUserAccount = async (userId) => {
    const migrationResults = {
        success: true,
        errors: [],
        migratedData: {
            expenses: 0,
            passiveIncomeEntries: 0,
            portfolioNavUpdates: 0,
            userSettings: false,
            guideData: false
        }
    };

    try {
        // Get all demo data from localStorage
        const demoData = {
            expenses: getLocalStorageData('expenses', []),
            currentExpense: getLocalStorageData('currentExpense', null),
            passiveIncomeEntries: getLocalStorageData('passiveIncomeEntries', []),
            portfolioNavUpdates: getLocalStorageData('portfolioNavUpdates', []),
            userSettings: getLocalStorageData('userSettings', { yield: 4, investmentPlanAmount: 800 }),
            smallestExpense: getLocalStorageData('smallestExpense', null),
            biggestExpense: getLocalStorageData('biggestExpense', null),
            investments: getLocalStorageData('investments', null),
            passiveIncome: getLocalStorageData('passiveIncome', null)
        };

        console.log('Starting demo data migration for user:', userId);
        console.log('Demo data found:', demoData);

        // Migrate expenses
        if (demoData.expenses && demoData.expenses.length > 0) {
            try {
                for (const expense of demoData.expenses) {
                    await addDoc(collection(db, "expenses"), {
                        userId: userId,
                        name: expense.name,
                        expenseType: expense.expenseType,
                        entries: expense.entries.map(entry => ({
                            uuid: entry.uuid,
                            costInCents: entry.costInCents,
                            loggedAt: entry.loggedAt instanceof Date ? entry.loggedAt : new Date(entry.loggedAt)
                        }))
                    });
                    migrationResults.migratedData.expenses++;
                }
                console.log(`Migrated ${migrationResults.migratedData.expenses} expenses`);
            } catch (error) {
                console.error('Error migrating expenses:', error);
                migrationResults.errors.push(`Failed to migrate expenses: ${error.message}`);
            }
        }

        // Migrate passive income entries
        if (demoData.passiveIncomeEntries && demoData.passiveIncomeEntries.length > 0) {
            try {
                for (const entry of demoData.passiveIncomeEntries) {
                    await addDoc(collection(db, "passiveIncomeEntries"), {
                        userId: userId,
                        amountInCents: entry.amountInCents,
                        loggedAt: entry.loggedAt instanceof Date ? entry.loggedAt : new Date(entry.loggedAt),
                        title: entry.title
                    });
                    migrationResults.migratedData.passiveIncomeEntries++;
                }
                console.log(`Migrated ${migrationResults.migratedData.passiveIncomeEntries} passive income entries`);
            } catch (error) {
                console.error('Error migrating passive income entries:', error);
                migrationResults.errors.push(`Failed to migrate passive income entries: ${error.message}`);
            }
        }

        // Migrate portfolio navigation updates
        if (demoData.portfolioNavUpdates && demoData.portfolioNavUpdates.length > 0) {
            try {
                for (const navUpdate of demoData.portfolioNavUpdates) {
                    await addDoc(collection(db, "portfolioNavUpdates"), {
                        userId: userId,
                        amountInCents: navUpdate.amountInCents,
                        loggedAt: navUpdate.loggedAt instanceof Date ? navUpdate.loggedAt : new Date(navUpdate.loggedAt),
                        isInvestment: navUpdate.isInvestment || false
                    });
                    migrationResults.migratedData.portfolioNavUpdates++;
                }
                console.log(`Migrated ${migrationResults.migratedData.portfolioNavUpdates} portfolio nav updates`);
            } catch (error) {
                console.error('Error migrating portfolio nav updates:', error);
                migrationResults.errors.push(`Failed to migrate portfolio nav updates: ${error.message}`);
            }
        }

        // Migrate user settings (update existing user_settings document)
        if (demoData.userSettings) {
            try {
                await setDoc(doc(db, "user_settings", userId), {
                    ...demoData.userSettings,
                    migratedFromDemo: true,
                    migrationDate: new Date()
                }, { merge: true });
                migrationResults.migratedData.userSettings = true;
                console.log('Migrated user settings');
            } catch (error) {
                console.error('Error migrating user settings:', error);
                migrationResults.errors.push(`Failed to migrate user settings: ${error.message}`);
            }
        }

        // Migrate guide data (store in user_settings as additional data)
        const guideData = {
            smallestExpense: demoData.smallestExpense,
            biggestExpense: demoData.biggestExpense,
            investments: demoData.investments,
            passiveIncome: demoData.passiveIncome
        };

        const hasGuideData = Object.values(guideData).some(data => data && (data.name || data.amount));
        if (hasGuideData) {
            try {
                await setDoc(doc(db, "user_settings", userId), {
                    guideData: guideData,
                    migratedFromDemo: true,
                    migrationDate: new Date()
                }, { merge: true });
                migrationResults.migratedData.guideData = true;
                console.log('Migrated guide data');
            } catch (error) {
                console.error('Error migrating guide data:', error);
                migrationResults.errors.push(`Failed to migrate guide data: ${error.message}`);
            }
        }

        // Clear demo data from localStorage after successful migration
        if (migrationResults.errors.length === 0) {
            clearDemoDataFromLocalStorage();
            console.log('Demo data cleared from localStorage');
        }

        console.log('Migration completed:', migrationResults);
        return migrationResults;

    } catch (error) {
        console.error('Error during demo data migration:', error);
        migrationResults.success = false;
        migrationResults.errors.push(`Migration failed: ${error.message}`);
        return migrationResults;
    }
};

/**
 * Helper function to safely get data from localStorage
 * @param {string} key - localStorage key
 * @param {*} defaultValue - Default value if key doesn't exist or is invalid
 * @returns {*} - Parsed data or default value
 */
const getLocalStorageData = (key, defaultValue) => {
    try {
        if (typeof window === 'undefined' || !window.localStorage) {
            return defaultValue;
        }
        
        const saved = window.localStorage.getItem(key);
        if (saved && saved !== 'undefined' && saved !== 'null') {
            return JSON.parse(saved);
        }
        return defaultValue;
    } catch (error) {
        console.warn(`Failed to parse localStorage value for key "${key}":`, error);
        return defaultValue;
    }
};

/**
 * Clears all demo data from localStorage
 */
const clearDemoDataFromLocalStorage = () => {
    const demoKeys = [
        'expenses',
        'currentExpense', 
        'passiveIncomeEntries',
        'portfolioNavUpdates',
        'userSettings',
        'smallestExpense',
        'biggestExpense',
        'investments',
        'passiveIncome',
        // remove onboarding chcklist items
        'kaizen-info-viewed',
        'demo-passive-income-opened' 
    ];

    demoKeys.forEach(key => {
        try {
            window.localStorage.removeItem(key);
        } catch (error) {
            console.warn(`Failed to remove localStorage key "${key}":`, error);
        }
    });
};

/**
 * Checks if there is demo data in localStorage that can be migrated
 * @returns {boolean} - True if demo data exists
 */
export const hasDemoDataToMigrate = () => {
    const demoKeys = ['expenses', 'passiveIncomeEntries', 'portfolioNavUpdates', 'userSettings'];
    return demoKeys.some(key => {
        try {
            const data = window.localStorage.getItem(key);
            return data && data !== 'undefined' && data !== 'null' && data !== '[]' && data !== '{}';
        } catch {
            return false;
        }
    });
};
